import { BOOKING_TYPE } from 'lib/constants';

export type ExpertNoviceParticipant = {
  _id?: string | null;
  status?: number | null;
  cancel?: number | null;
  Submission?: { _id?: string | null; role?: string | null } | null;
};

export const isExpertNoviceSessionBooking = (
  booking:
    | {
        type?: number | null;
        config?: { expert_novice_study?: boolean | null; session?: { type?: number | null } | null } | null;
      }
    | null
    | undefined,
  labsEnabled: boolean,
): boolean =>
  labsEnabled &&
  booking?.config?.expert_novice_study === true &&
  booking.config.session?.type === 2 &&
  (booking.type === BOOKING_TYPE.IN_PERSON ||
    booking.type === BOOKING_TYPE.REMOTE ||
    booking.type === BOOKING_TYPE.LONGITUDINAL_STUDY);

/** Only non-cancelled confirmations hold places. Invitations remain visible but never fill one. */
export const expertNoviceSessionSummary = <T extends ExpertNoviceParticipant>(
  participants: readonly (T | null)[] = [],
) => {
  const seen = new Set<string>();
  const current = [...participants]
    .sort((a, b) => Number(b?.status === 1) - Number(a?.status === 1))
    .filter((participant): participant is T => {
      if (!participant || participant.cancel !== 0 || (participant.status !== 1 && participant.status !== 4))
        return false;
      // A cancelled/reinvited submission can have several historical session rows.
      const id = participant.Submission?._id ?? participant._id;
      if (id && seen.has(id)) return false;
      if (id) seen.add(id);
      return true;
    });
  const expert = current.filter(p => p.Submission?.role === 'expert');
  const novice = current.filter(p => p.Submission?.role === 'novice');
  const unassigned = current.filter(p => !['expert', 'novice'].includes(p.Submission?.role ?? ''));
  const expertsConfirmed = expert.filter(p => p.status === 1).length;
  const novicesConfirmed = novice.filter(p => p.status === 1).length;
  const confirmed = current.filter(p => p.status === 1).length;
  // Two people in the same role is a clash the researcher resolves by moving someone. A confirmed
  // attendee with no role yet is a different problem with a different fix, so it gets its own copy.
  const conflict = expertsConfirmed > 1 || novicesConfirmed > 1;
  const unassignedConfirmed = unassigned.filter(p => p.status === 1).length;
  const full = !conflict && unassignedConfirmed === 0 && expertsConfirmed === 1 && novicesConfirmed === 1;
  const label = (() => {
    if (conflict) return 'Check roles';
    if (unassignedConfirmed) return 'Assign roles';
    if (full) return 'Full';
    if (expertsConfirmed) return 'Needs user';
    if (novicesConfirmed) return 'Needs assistant';
    return 'Needs both';
  })();
  const tone = (() => {
    if (conflict || unassignedConfirmed) return 'conflict';
    if (full) return 'full';
    return 'open';
  })();
  return {
    expert,
    novice,
    unassigned,
    expertsConfirmed,
    novicesConfirmed,
    unassignedConfirmed,
    confirmed,
    conflict,
    full,
    label,
    tone,
  } as const;
};
